import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface ImagePreviewModalProps {
  imageUrl: string;
  fileName: string;
  onClose: () => void;
}

export const ImagePreviewModal = ({
  imageUrl,
  fileName,
  onClose,
}: ImagePreviewModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle focus management when modal opens
  useEffect(() => {
    // Set focus to the modal when it opens
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Store previous active element to restore focus later
    const previousActiveElement = document.activeElement;

    return () => {
      // Restore focus when modal closes
      if (previousActiveElement instanceof HTMLElement) {
        previousActiveElement.focus();
      }
    };
  }, []);

  // Close modal when pressing Escape key but prevent propagation
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-preview-title"
      ref={modalRef}
      tabIndex={-1}
    >
      {/* Backdrop div */}
      <div
        className="absolute inset-0 w-full h-full cursor-default"
        onClick={onClose}
        role="presentation"
      />

      <div
        className="relative max-w-[90vw] max-h-[90vh] bg-white rounded-lg p-1 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <button
          type="button"
          className="absolute top-2 right-2 z-10 p-1 bg-black/30 rounded-full text-white hover:bg-black/50 transition-colors"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center animate-pulse">
                <div className="w-12 h-12 bg-gray-300 rounded-full mb-2" />
                <div className="h-4 w-24 bg-gray-300 rounded" />
              </div>
            </div>
          )}

          <img
            src={imageUrl}
            alt={fileName}
            className="max-w-full max-h-[85vh] object-contain rounded"
            onLoad={() => setIsLoading(false)}
          />
        </div>

        <div
          className="w-full text-center mt-2 text-sm text-gray-700 truncate"
          id="image-preview-title"
        >
          {fileName}
        </div>
      </div>
    </div>
  );
};
